/**
 * Shared ExecutionContext for TALKTO+ Enterprise V2.
 *
 * Provides central navigation and presentation context across all 8 surfaces:
 * - tenantId, organizationId, programId, programName, availablePrograms, workspaceId, timeRangeDays, locale.
 *
 * Invariants:
 * 1. ExecutionContext is presentation and navigation context ONLY.
 * 2. Authorization is strictly validated on backend API requests; client-side context NEVER grants authorization.
 * 3. In DEMO mode, DEMO_PROGRAMS exist as explicit synthetic fixtures.
 * 4. In PRODUCTION mode:
 *    - Frontend NEVER defines or fabricates authorized programs.
 *    - If authoritative backend program discovery has not supplied data, availablePrograms is strictly empty ([]).
 *    - Demo fixtures are NEVER exposed in PRODUCTION mode.
 */

export const DEMO_PROGRAMS = [
  { id: 'demo_northstar', name: 'Northstar Program', status: 'active' },
  { id: 'demo_supply_chain', name: 'Supply Chain Resiliency', status: 'active' },
  { id: 'demo_cloud_migration', name: 'Cloud Core Migration', status: 'active' },
];

export function resolveExecutionContext({
  user,
  productMode = 'DEMO',
  productionPrograms,
  programId,
  workspaceId,
  timeRangeDays = 14,
  locale = 'en',
} = {}) {
  const tenantId = user?.tenant_id || user?.tenantId || null;
  const organizationId = user?.organization_id || user?.organizationId || null;

  let availablePrograms = [];
  let selectedProgramId = null;
  let programName = null;

  if (productMode === 'PRODUCTION') {
    // Production mode: authority strictly belongs to backend projection.
    // If no programs provided by backend, availablePrograms is empty.
    availablePrograms = Array.isArray(productionPrograms) ? productionPrograms : [];
    if (availablePrograms.length > 0) {
      const match = programId ? availablePrograms.find((p) => p.id === programId) : null;
      const selected = match || availablePrograms[0];
      selectedProgramId = selected.id;
      programName = selected.name || null;
    } else {
      selectedProgramId = programId || null;
      programName = null;
    }
  } else {
    // Demo mode: synthetic fixtures for interactive demo & exploration.
    availablePrograms = DEMO_PROGRAMS;
    const match = programId ? availablePrograms.find((p) => p.id === programId) : null;
    const selected = match || availablePrograms[0];
    selectedProgramId = selected.id;
    programName = selected.name;
  }

  return {
    tenantId,
    organizationId,
    programId: selectedProgramId,
    programName,
    availablePrograms,
    workspaceId: workspaceId || undefined,
    timeRangeDays: Number(timeRangeDays) || 14,
    locale: locale || 'en',
  };
}
